import React from 'react';
import { formatDayWithNumber, getWeekStartDate } from '../../utils/helpers.ts';

/**
 * FixedDayHeader - A reusable component for displaying the current visible day
 * with smooth animations when scrolling between days.
 * 
 * @param {Object} props
 * @param {string} props.currentVisibleDay - The currently visible day (e.g., 'monday')
 * @param {Date} props.currentDate - The current date for calculating week context
 * @param {string} props.animationDirection - Animation direction ('slideUp' or 'slideDown')
 * @param {boolean} props.show - Whether to show the header (default: true)
 * @param {string} props.className - Additional CSS classes
 */
const FixedDayHeader = ({
    currentVisibleDay,
    currentDate,
    animationDirection = 'slideUp',
    show = true,
    className = ''
}) => {
    if (!show || !currentVisibleDay || !currentDate) {
        return null;
    }

    const dayInfo = formatDayWithNumber(currentVisibleDay, getWeekStartDate(currentDate));

    return (
        <div className={`sticky top-0 w-full text-xl font-bold tracking-wide py-3 px-4 capitalize border-b-2 bg-gradient-to-r from-grey-50 to-white text-grey-700 shadow-lg overflow-hidden ${className}`}>
            <div className="relative">
                <div
                    key={currentVisibleDay}
                    className="animate-day-transition flex items-center gap-2"
                    style={{
                        animation: `${animationDirection} 0.3s ease-out`
                    }}
                >
                    <span>{dayInfo.shortName}</span>
                    <span className="text-lg font-normal text-slate-800 opacity-90">
                        {dayInfo.dayNumber}
                    </span>
                </div>
            </div>

            {/* CSS Keyframes for animations */}
            <style jsx>{`
                @keyframes slideUp {
                    0% {
                        opacity: 0;
                        transform: translateY(20px) rotateX(-30deg);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0) rotateX(0);
                    }
                }
                @keyframes slideDown {
                    0% {
                        opacity: 0;
                        transform: translateY(-20px) rotateX(30deg);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0) rotateX(0);
                    }
                }
                .animate-day-transition {
                    transform-origin: center center;
                    perspective: 1000px;
                }
            `}</style>
        </div>
    );
};

export default FixedDayHeader;
